import dayjs, { Dayjs } from 'dayjs';
import { useScheduleActionLock } from '../../../hook/useScheduleActionLock';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRepeatScheduleAPI, getMemberListForCenterAPI } from '../../../api/schedule';
import {
    buildWritableScheduleMemberOptions,
    isScheduleMemberPickerReadOnly,
    refreshSelectedScheduleMembers,
} from '../memberPicker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Col, isSameDate, Row } from '../../../lib/utils';
import { Body3Regular, Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import WhiteSquareButton from '../../Button/WhiteSquareButton';
import ColorSquareButton from '../../Button/ColorSquareButton';
import SvgIcon from '../../SvgIcon';
import CreateRepeatScheduleLeft from './CreateRepeatScheduleLeft';
import CreateRepeatScheduleRight from './CreateRepeatScheduleRight';
import ModalMembershipCreate from '../../MemberProfile/ModalMembershipCreate';
import ModalRegisterPass from '../../../scenes/ClassPass/ModalRegisterPass/ModalRegisterPass';
import {
    getSharedScheduleErrorMessage,
    getStableSharedScheduleOperation,
    shouldDiscardSharedScheduleOperation,
    StableSharedScheduleOperation,
    useScheduleMembershipEntitlements,
} from '../../../features/sharedMembership';

import Calendar from '../../Calendar/Calendar';
import {
    buildScheduleConfirmAlertContent,
    resolveScheduleSubmitDecision,
} from '../scheduleSubmitPlan';
import {
    findScheduleDateWarnings,
    formatScheduleDateWarningMessage,
    formatSharedDateBlockMessage,
} from '../scheduleDateWarning';
import { dispatchScheduleSubmitDecision } from '../scheduleConfirmAttempt';
import { useScheduleConfirmDialog } from '../scheduleConfirmDialog';
import useMembershipPauseCreateGate from '../../MembershipPause/useMembershipPauseCreateGate';
import { compactTargets, toPtPauseGateTarget } from '../../../features/membershipPause/createGate';

interface CalculatedSchedule {
    startTime: Dayjs;
    endTime: Dayjs;
}

interface ScheduleDate {
    startTime: Dayjs;
    endTime: Dayjs;
}

const MAX_REPEAT_SCHEDULE_COUNT = 200;
const REPEAT_SCHEDULE_LIMIT_MESSAGE = `반복 수업은 한 번에 최대 ${MAX_REPEAT_SCHEDULE_COUNT}개까지 등록할 수 있습니다\n횟수를 나눠서 등록해주세요`;

/**
 * 등록 버튼을 누른 순간 한 번 만든 **보낼 요청 내용**.
 *
 * 예전에는 mutation 이 선택 날짜·반복 목록·강사·센터·수강권을 상태에서 다시 읽었다.
 * 날짜 경고 창이 떠 있는 동안 그 상태가 바뀌면 확인한 내용과 다른 일정이 나간다.
 * 그래서 전부 이 객체에 굳혀 판정·확인 창·전송이 같은 값을 쓴다.
 */
type RepeatScheduleSubmission = {
    centerId: string;
    coachId: string;
    coachLabel: string;
    memberId: string;
    membership: any;
    baseStartDate: string;
    /** 요청 body 의 scheduleTimes 그대로(dayjs 객체 — 전송 때 ISO 문자열로 직렬화된다). */
    scheduleTimes: CalculatedSchedule[];
    intentKey: string;
};

const CreateRepeatSchedule = ({
    coachOption,
    authorization,
    authObject,
    createAlert,
    selectedSchedule,
    calendarType,
    calendarSelectedDay,
    calendarScrollY,
    sharedMembershipStaffBookingEnabled,
}: any) => {
    /** 스케줄 동작 잠금(생성) — 진입은 경로 가드가, 저장 직전 재검사(30분 만료)는 여기서 한다 */
    const scheduleLock = useScheduleActionLock('schedule.create');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const submissionLockRef = useRef(false);
    const sharedOperationRef = useRef<StableSharedScheduleOperation | null>(null);
    /**
     * 등록 직전 확인 창은 공통 알럿이 아니라 이 화면의 지역 모달로 띄운다. 공통 알럿은 늦게 온 다른
     * 알럿이 앞 확인 콜백을 물려받아 수업을 보낼 수 있다(scheduleConfirmDialog 주석).
     */
    const { openConfirm, closeConfirm, confirmDialog } = useScheduleConfirmDialog();
    // 수강권 일시정지 — 반복 회차 중 정지 기간에 드는 것이 있으면 확인 창(P3)
    const pauseGate = useMembershipPauseCreateGate({ createAlert });
    /** 생성 실패 안내. 「풀고 등록」 뒤 실패면 정지가 풀렸다는 문구를 앞에 붙인다. */
    const alertCreateFailure = (title: string, message: string, ...rest: any[]) =>
        createAlert(title, pauseGate.decorateFailureMessage(message), ...rest);
    const currentTime = dayjs(selectedSchedule?.scheduleStartTime);
    const roundedTime = currentTime.minute(Math.floor(currentTime.minute() / 10) * 10).second(0);
    const currentTimeAdd50 = dayjs(roundedTime).add(50, 'minute');

    /**
     * coach : 담당강사
     * selectedDay: 첫등록일
     * startTime : 시작 시간
     * endTime : 종료시간
     * oneOnOneMemberShipIndex : 멤버쉽의 현재 index
     * maxRepeatNumber : 반복 횟수중 최대회수
     * repeatScheduleList : 정제된 반복 스케줄 리스트
     */
    const [coach, setCoach] = useState({
        value: selectedSchedule ? selectedSchedule?.coachId : '',
        label: selectedSchedule ? selectedSchedule?.coachName : '강사를 선택해주세요!',
    });
    const [selectedDay, setSelectedDay] = useState<any>(
        selectedSchedule ? new Date(selectedSchedule?.scheduleStartTime) : new Date()
    );
    const [repeatNumber, setRepeatNumber] = useState<string>('');
    const [startTime, setStartTime] = useState({ value: roundedTime, label: dayjs(roundedTime).format('A hh:mm') });
    const [endTime, setEndTime] = useState({
        value: currentTimeAdd50,
        label: dayjs(currentTimeAdd50).format('A hh:mm'),
    });
    const [currentMemberShipIndex, setCurrentMemberShipIndex] = useState<number>(1);
    const [maxRepeatNumber, setMaxRepeatNumber] = useState<number>(0);
    const [repeatScheduleList, setRepeatScheduleList] = useState<any>([]);

    // 반복요일
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const initializeDays = () => {
        return dayNames.map((day, index) => ({
            week: index.toString(),
            value: day,
            isSelected: false, // 선택 되엇던 요일
            startTime: { value: roundedTime, label: dayjs(roundedTime).format('A hh:mm') },
            endTime: {
                value: currentTimeAdd50,
                label: dayjs(currentTimeAdd50).format('A hh:mm'),
            },
        }));
    };
    const [daysArr, setDaysArr] = useState(initializeDays);
    const [isEveryDaySameTime, setIsEveryDaySameTime] = useState<boolean>(true);

    /**
     * searchText: 회원 검색
     * memberList: 강사의 연결된 모든 회원의 리스트
     * filteredMemberList : 검색결과가 반영된 회원 리스트
     * selectedMember: 선택된 회원
     */
    const [searchText, setSearchText] = useState<string>('');
    const [memberList, setMemberList] = useState<any>([]);
    const [filteredMemberList, setFilteredMemberList] = useState<any>([]);
    const [selectedMember, setSelectedMember] = useState<any>([]);

    const {
        selectedMembers: scheduleSelectedMember,
        excludedSharedMembershipCounts,
        isReady: areMembershipEntitlementsReady,
        isLoading: areMembershipEntitlementsLoading,
        isError: hasMembershipEntitlementsError,
        refetch: refetchMembershipEntitlements,
    } = useScheduleMembershipEntitlements({
        selectedMembers: selectedMember,
        centerId: String(authObject?.centerId || ''),
        coachId: String(coach?.value || ''),
        enabled: sharedMembershipStaffBookingEnabled === true,
    });

    const [isCalendar, setIsCalendar] = useState<boolean>(false); // 달력 boolean
    const [timeList, setTimeList] = useState<any>(null); // 10분단위 시간 배열

    // 수강권 등록 모달 열기
    const [isModalMembershipCreateVisible, setIsModalMembershipCreateVisible] = useState(false);

    // 회원 수강권 새로 생성 모달
    const [isRegisterPassModal, setIsRegisterPassModal] = useState(false);

    const [selectedMemberName, setSelectedMemberName] = useState('');
    const [selectedMemberId, setSelectedMemberId] = useState('');

    // 회원 검색 이벤트
    const onChangeText = (e: any) => {
        setSearchText(e.target.value);
    };

    // 담당 강사 선택
    const handleCoachSelect = (option: any) => {
        setCoach(option);
    };

    // 시작 시간 선택
    const handleStartTimeSelect = (option: any) => {
        setStartTime(option);
    };

    // 종료 시간 선택
    const handleEndTimeSelect = (option: any) => {
        setEndTime(option);
    };

    // 멤버 선택
    const handleMemberSelect = (option: any) => {
        if (isScheduleMemberPickerReadOnly(option)) return;
        setSearchText('');
        let cloneSelectedMember = _.cloneDeep(selectedMember);
        cloneSelectedMember.push(option);
        setSelectedMember(cloneSelectedMember);
    };

    // 선택된 회원 지우는 함수
    const onClickDeleteSelectedMember = (value: string) => {
        const filterSelectedMemberList = selectedMember.filter((e: any) => e.value !== value);
        setSelectedMember(filterSelectedMemberList);
        setMaxRepeatNumber(0);
    };

    // 반복횟수 -> 최대횟수 클릭이벤트
    const onChangeRepeatCount = (repeatNumberString: string) => {
        if (repeatNumberString === '') {
            setRepeatNumber('');
            return;
        }

        let repeatNumber = parseInt(repeatNumberString.replace(/[^0-9]/g, ''));

        if (Number.isNaN(repeatNumber)) {
            setRepeatNumber('');
            return;
        }

        if (repeatNumber < 1) {
            repeatNumber = 1;
        }
        if (repeatNumber > maxRepeatNumber) {
            repeatNumber = maxRepeatNumber;
        }

        setRepeatNumber(repeatNumber.toString());
    };

    // 반복횟수 -> 최대횟수 클릭이벤트
    const onClickMaxRepeatCount = () => {
        if (maxRepeatNumber) {
            setRepeatNumber(maxRepeatNumber.toString());
        }
    };

    // 반복요일 클릭이벤트
    const onClickRepeatDay = (item: any) => {
        let cloneDayjsArr = _.cloneDeep(daysArr);
        let findIndex: any = cloneDayjsArr.findIndex((e: any) => e.value === item.value);

        cloneDayjsArr[findIndex].isSelected = !cloneDayjsArr[findIndex].isSelected;

        // 요일이 해제가 되면 해당요일에 시간을 초기화해준다
        for (let i = 0; i < cloneDayjsArr.length; i++) {
            if (cloneDayjsArr[i]?.isSelected === false) {
                cloneDayjsArr[i].startTime = { value: roundedTime, label: dayjs(roundedTime).format('A hh:mm') };
                cloneDayjsArr[i].endTime = {
                    value: currentTimeAdd50,
                    label: dayjs(currentTimeAdd50).format('A hh:mm'),
                };
            }
        }

        setDaysArr(cloneDayjsArr);
    };

    // 수강권 추가하기 버튼 클릭이벤트
    const onClickClassPassPage = (selectedMember: any) => {
        setSelectedMemberId(selectedMember.value);
        setSelectedMemberName(selectedMember.label);
        setIsModalMembershipCreateVisible(true);
    };

    //  회원 리스트 조회 API : (GET)
    const getMemberListForCenter = useQuery(
        ['getMemberListForCenterAPI', coach?.value],
        async () => await getMemberListForCenterAPI(authObject?.centerId, coach?.value, authorization),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const memberListToRes = buildWritableScheduleMemberOptions(
                        res?.data?.memberDataList
                    );
                    setMemberList(memberListToRes);
                    setFilteredMemberList(memberListToRes);
                    setSelectedMember(
                        refreshSelectedScheduleMembers(selectedMember, memberListToRes)
                    );
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!coach?.value,
            // refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    //반복 수업 생성 API : (POST)
    /** 반복 요청 내용. 의도 키도 여기서 같이 굳힌다(규칙은 예전과 같다). */
    const buildRepeatScheduleSubmission = (): RepeatScheduleSubmission => {
        const membership = scheduleSelectedMember[0]?.memberships[currentMemberShipIndex - 1];
        const centerId = authObject?.centerId;
        const coachId = coach?.value;
        const memberId = scheduleSelectedMember[0]?.value;
        const baseStartDate = dayjs(selectedDay).toISOString();
        // 배열은 복사해 굳힌다. 원소(dayjs)는 불변이라 그대로 둬도 바뀌지 않는다.
        const scheduleTimes: CalculatedSchedule[] = [...repeatScheduleList];
        const intentKey = JSON.stringify({
            kind: 'REPEAT',
            centerId: String(centerId || ''),
            coachId: String(coachId || ''),
            memberId: String(memberId || ''),
            membershipId: String(membership?.id || membership?._id || ''),
            membershipSharePoolId: String(membership?.membershipSharePoolId || ''),
            baseStartDate,
            scheduleTimes: scheduleTimes.map((schedule: any) => ({
                startTime: dayjs(schedule.startTime).toISOString(),
                endTime: dayjs(schedule.endTime).toISOString(),
            })),
        });
        return {
            centerId,
            coachId,
            coachLabel: coach?.label,
            memberId,
            membership,
            baseStartDate,
            scheduleTimes,
            intentKey,
        };
    };

    const createRepeatSchedule = useMutation({
        mutationFn: async (submission: RepeatScheduleSubmission) => {
            const { membership } = submission;
            const sharedOperation = getStableSharedScheduleOperation(
                sharedOperationRef.current,
                submission.intentKey,
                membership,
            );
            sharedOperationRef.current = sharedOperation ?? null;
            return await createRepeatScheduleAPI(
                submission.centerId,
                submission.baseStartDate,
                submission.coachId,
                submission.memberId,
                membership?.id,
                submission.scheduleTimes,
                authorization,
                sharedOperation?.writeContext,
            );
        },
        onSuccess: (res, submission) => {
            const sharedErrorMessage = getSharedScheduleErrorMessage(res);
            if (res.status === 200 || res.status === 201) {
                sharedOperationRef.current = null;
                // 반복 스케줄 생성은 수강권 잔여회차를 바꾸므로 회원 상세 수강권 쿼리 무효화
                queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
                queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] });
                queryClient.invalidateQueries({ queryKey: ['sharedMembership', 'staffEntitlements'] });
                queryClient.invalidateQueries({ queryKey: ['getSmartRangeSchedulesAPI'] });
                queryClient.invalidateQueries({ queryKey: ['getListRangeSchedulesAPI'] });
                const startTime = dayjs(submission.scheduleTimes[0].startTime).toISOString();
                navigate('/schedule', {
                    state: { calendarType, calendarTimeInfo: startTime },
                    replace: true,
                });
            } else if (sharedErrorMessage) {
                if (shouldDiscardSharedScheduleOperation(res)) {
                    sharedOperationRef.current = null;
                }
                queryClient.invalidateQueries({ queryKey: ['sharedMembership', 'staffEntitlements'] });
                alertCreateFailure('공유 수강권 확인', sharedErrorMessage, true);
            } else if (res.status === 410 || res.status === 420 || res.status === 430) {
                alertCreateFailure(
                    '중복',
                    `선택된 담당 강사 ${submission.coachLabel}님이\n해당시간에 중복된 스케줄이 존재합니다`,
                    true
                );
            } else if (res.status === 400) {
                alertCreateFailure('입력 오류', REPEAT_SCHEDULE_LIMIT_MESSAGE, true);
            } else {
                alertCreateFailure('오류', `서버 내부 오류\n 지속적인 오류발생시 고객센터로 문의바랍니다`, true);
            }
        },
        onError: () => {
            alertCreateFailure('오류', '반복 일정 생성 요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요', true);
        },
        onSettled: () => {
            submissionLockRef.current = false;
        },
    });

    const submitRepeatSchedule = async (submission: RepeatScheduleSubmission) => {
        if (submissionLockRef.current) return;
        // 스케줄 동작 잠금 재검사 — 정지 해제(첫 부수 쓰기)보다 **먼저** 본다(설계 §6-6)
        if (scheduleLock.requiresCheck() && !(await scheduleLock.ensure())) return;
        if (submissionLockRef.current) return;
        // 보내기 직전 정지 확인 — 반복 회차 시각 전부로 판정한다(취소·해제 실패면 보내지 않는다)
        const proceed = await pauseGate.confirmBeforeCreate(
            compactTargets([
                toPtPauseGateTarget(
                    submission.membership,
                    String(selectedMember[0]?.label || ''),
                    submission.scheduleTimes.map((schedule: any) => dayjs(schedule.startTime).toISOString()),
                ),
            ]),
        );
        if (!proceed || submissionLockRef.current) return;
        submissionLockRef.current = true;
        createRepeatSchedule.mutate(submission);
    };

    // 반복 수업 등록 버튼 클릭이벤트
    const onClickRegisterRepeatScheduleButton = () => {
        if (submissionLockRef.current) return;
        // 새 제출 시도는 앞 확인 창의 시도를 끊는다(어떤 검사에서 멈추든 앞 "그래도 잡기"가 남지 않게).
        closeConfirm();

        if (coach.value === '') {
            createAlert('필수 입력', '담당 강사를 선택해주세요', true);
            return;
        }

        if (repeatNumber === '') {
            createAlert('필수 입력', '반복 횟수를 입력해주세요', true);
            return;
        }

        if (selectedMember.length === 0) {
            createAlert('필수 입력', '회원을 선택해주세요', true);
            return;
        }

        if (selectedMember.some(isScheduleMemberPickerReadOnly)) {
            createAlert('등록 불가', '탈퇴 회원은 새 수업에 등록할 수 없습니다', true);
            return;
        }

        if (
            sharedMembershipStaffBookingEnabled === true &&
            !areMembershipEntitlementsReady
        ) {
            const isEntitlementLoading =
                areMembershipEntitlementsLoading && !hasMembershipEntitlementsError;
            // 조회가 에러로 굳으면 react-query가 스스로 재조회하지 않는다.
            // '다시 시도' 안내가 실제로 동작하도록 재조회 버튼을 함께 준다.
            createAlert(
                '수강권 확인 필요',
                isEntitlementLoading
                    ? '공유 수강권 사용 권한을 확인하고 있습니다\n잠시 후 다시 시도해주세요'
                    : '공유 수강권 사용 권한을 확인하지 못했습니다\n네트워크 상태를 확인한 뒤 다시 조회해주세요',
                true,
                isEntitlementLoading ? '확인' : '다시 조회',
                isEntitlementLoading ? undefined : () => refetchMembershipEntitlements(),
            );
            return;
        }

        if (Number(repeatNumber) > MAX_REPEAT_SCHEDULE_COUNT || repeatScheduleList.length > MAX_REPEAT_SCHEDULE_COUNT) {
            createAlert('입력 오류', REPEAT_SCHEDULE_LIMIT_MESSAGE, true);
            return;
        }

        const findSelectedIndex = daysArr.findIndex((item: any) => item.isSelected === true);

        if (findSelectedIndex === -1) {
            createAlert('필수 입력', '반복 요일을 선택해주세요', true);
            return;
        }

        let findNullMembership = scheduleSelectedMember.findIndex(
            (item: any) => item?.memberships.length === 0,
        );

        if (findNullMembership !== -1) {
            // 공유 수강권은 담당 강사 수업에서만 목록에 오른다. 다른 강사를 고른 화면에서는
            // 그 사실을 알려야 직원이 "공유가 안 된다"고 오인하지 않는다.
            createAlert(
                '필수 입력',
                excludedSharedMembershipCounts[findNullMembership] > 0
                    ? '이 회원의 공유 수강권은 담당 강사의 수업에서만 등록할 수 있습니다\n수강권의 담당 강사를 선택하거나 개인 수강권을 사용해주세요'
                    : '수강권이 없는 회원이 존재합니다\n반드시 수강권을 만들고 일정을 만들어주세요',
                true
            );
            return;
        }

        let isUnavailableTime = false;
        repeatScheduleList.map((repeatSchedule: any) => {
            if (
                dayjs(repeatSchedule.startTime).isAfter(repeatSchedule.endTime) ||
                dayjs(repeatSchedule.startTime).isSame(repeatSchedule.endTime)
            ) {
                isUnavailableTime = true;
            }
        });
        if (isUnavailableTime) {
            createAlert(
                '입력 오류',
                '시작 시간은 종료 시간과 같거나 늦을 수 없습니다\n다시 한번 확인해주세요!',
                '확인',
                () => {}
            );
            return;
        }

        // 보낼 요청 내용을 여기서 한 번 만든다. 판정·확인 창·전송이 모두 이 객체를 쓴다.
        const submission = buildRepeatScheduleSubmission();
        const dateWarnings = findScheduleDateWarnings(
            [
                {
                    memberId: submission.memberId,
                    memberName: selectedMember[0]?.label,
                    membershipId: submission.membership?.id,
                    membershipSharePoolId: submission.membership?.membershipSharePoolId,
                    times: submission.scheduleTimes,
                },
            ],
            scheduleSelectedMember[0]?.memberships,
        );

        // 이 화면에는 "다 쓴 수강권" 창이 없어 제출 계획은 늘 바로 제출이다(대상은 쓰지 않는다).
        // 공유는 한 회차라도 기간 밖이면 서버가 반복 전체를 거절하므로 차단(전송 0회)이다.
        // 개인만 걸리면 해당 회차 수와 첫 날짜를 보여 주는 확인 창이다.
        dispatchScheduleSubmitDecision({
            decision: resolveScheduleSubmitDecision(
                { kind: 'SUBMIT', submit: 'ONE_ON_ONE' },
                dateWarnings,
            ),
            createAlert,
            openConfirm,
            closeConfirm,
            send: () => submitRepeatSchedule(submission),
            buildConfirmContent: ({ exhausted, dateWarnings: personalWarnings }) =>
                buildScheduleConfirmAlertContent({
                    exhausted,
                    dateWarningMessage: formatScheduleDateWarningMessage(personalWarnings),
                }),
            buildSharedDateBlockMessage: (sharedWarnings) =>
                formatSharedDateBlockMessage(sharedWarnings),
        });
    };

    //스케줄 리스트 계산
    const calculateScheduleDates = (
        startStandardDate: any, // 시작일자
        weeklySchedules: any, // 요일리스트
        countOfSchedules: any // 횟수
    ) => {
        // 그주의 월요일
        const firstWeekStandardDate = dayjs(startStandardDate).clone().startOf('week');
        weeklySchedules.sort(function (a: any, b: any) {
            return parseInt(a.week) - parseInt(b.week);
        });
        const calculatedWeeklySchedules = weeklySchedules.map((schedule: any) => {
            const startHour = parseInt(dayjs(schedule.startTime.value).format('HH'));
            const startMinute = parseInt(dayjs(schedule.startTime.value).format('mm'));
            const endHour = parseInt(dayjs(schedule.endTime.value).format('HH'));
            const endMinute = parseInt(dayjs(schedule.endTime.value).format('mm'));
            const offsetStartMinute = schedule.week * 24 * 60 + startHour * 60 + startMinute;
            const offsetEndMinute = schedule.week * 24 * 60 + endHour * 60 + endMinute;
            const startTime = firstWeekStandardDate.clone().add(offsetStartMinute, 'minutes');
            const endTime = firstWeekStandardDate.clone().add(offsetEndMinute, 'minutes');
            return { startTime, endTime };
        });
        const scheduleDates: any = [];
        let weekIndex = 0;
        while (scheduleDates.length < countOfSchedules) {
            calculatedWeeklySchedules.map((schedule: any) => {
                // 결과배열에 저장된 스케쥴의 갯수가 사용자가 입력한 횟수 이상이 될때 continue;
                if (scheduleDates.length >= countOfSchedules) return;
                const startTime = schedule.startTime.clone().add(weekIndex, 'weeks');
                // 계산된 startTime이 시작 기준 시간보다 이전이라면 continue;
                if (startTime.isBefore(startStandardDate)) return;
                const endTime = schedule.endTime.clone().add(weekIndex, 'weeks');
                scheduleDates.push({ startTime, endTime });
            });
            weekIndex++;
        }

        return scheduleDates;
    };

    // 반복 수업 스케줄 리스트 정제
    useEffect(() => {
        if (daysArr) {
            const filterList = daysArr.filter((item: any) => item.isSelected === true);

            if (repeatNumber !== '') {
                if (filterList.length > 0) {
                    const scheduleList = calculateScheduleDates(
                        dayjs(selectedDay).format('YYYY-MM-DD'),
                        filterList,
                        parseInt(repeatNumber)
                    );
                    if (scheduleList) {
                        setRepeatScheduleList(scheduleList);
                    }
                }
            }
        }
    }, [daysArr, repeatNumber]);

    // 수강권의 최대회수 탐색
    useEffect(() => {
        if (scheduleSelectedMember) {
            if (scheduleSelectedMember[0]?.memberships) {
                const currentMemberShip =
                    scheduleSelectedMember[0]?.memberships[currentMemberShipIndex - 1];
                // setMaxRepeatNumber(currentMemberShip?.remainSession);
                const canMakeScheduleSession = Number(currentMemberShip?.canMakeScheduleSession ?? 0);
                setMaxRepeatNumber(Math.min(canMakeScheduleSession, MAX_REPEAT_SCHEDULE_COUNT));
            }
        }
    }, [scheduleSelectedMember, currentMemberShipIndex]);

    useEffect(() => {
        if (repeatNumber === '') return;

        if (maxRepeatNumber <= 0) {
            setRepeatNumber('');
            return;
        }

        if (Number(repeatNumber) > maxRepeatNumber) {
            setRepeatNumber(maxRepeatNumber.toString());
        }
    }, [maxRepeatNumber, repeatNumber]);

    // 시작 시간이 변할때 endTime정제
    useEffect(() => {
        if (startTime) {
            const add50Min: Dayjs = dayjs(startTime.value).add(50, 'minute');

            if (
                add50Min.isAfter(dayjs(startTime.value).endOf('day')) ||
                isSameDate(add50Min, dayjs(startTime.value).endOf('day'))
            ) {
                setEndTime({
                    value: dayjs().endOf('day').add(1, 'millisecond'),
                    label: dayjs().endOf('day').add(1, 'millisecond').format('A hh:mm'),
                });
            } else {
                setEndTime({ value: dayjs(add50Min), label: dayjs(add50Min).format('A hh:mm') });
            }
        }
    }, [startTime]);

    // 회원이름 검색 정제
    useEffect(() => {
        if (memberList) {
            if (searchText !== '') {
                let filterMemberList = memberList.filter((e: any) => {
                    return (
                        e.label.includes(searchText) &&
                        !selectedMember.some((selected: any) => selected.value === e.value)
                    );
                });

                setFilteredMemberList(filterMemberList);
            } else {
                let filterMemberList = memberList.filter((e: any) => {
                    return !selectedMember.some((selected: any) => selected.value === e.value);
                });

                setFilteredMemberList(filterMemberList);
            }
        }
    }, [searchText, selectedMember]);

    // 시간 10분단위로 생성
    useEffect(() => {
        // 시간생성
        const generateTimeSlots = () => {
            const timeSlots = [];
            const start = dayjs().startOf('day').hour(0).minute(0);
            for (let i = 0; i < 144; i++) {
                // 24 hours * 6 (10분 간격)
                const time = start.add(i * 10, 'minute');
                const formattedTime = time.format('A hh:mm'); // 오전/오후 표시
                timeSlots.push({ value: time, label: formattedTime });
            }
            return timeSlots;
        };

        const timeSlots = generateTimeSlots();
        setTimeList(timeSlots);
    }, []);

    return (
        <Col style={{ paddingLeft: 101, paddingTop: 56, paddingRight: 101, paddingBottom: 64 }}>
            {/* 스케줄 동작 잠금 — 저장 직전 재검사 카드(body 포털) */}
            {scheduleLock.gate}
            {/* 등록 직전 확인 창(화면 지역, 고정 위치 덮개) */}
            {confirmDialog}
            {pauseGate.modal}
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Row style={{ alignItems: 'center' }}>
                    <div
                        style={{ cursor: 'pointer', marginRight: 16, display: 'flex', alignItems: 'center' }}
                        onClick={() => {
                            navigate('/schedule', {
                                state: { calendarType, calendarSelectedDay, calendarScrollY },
                                replace: true,
                            });
                        }}
                    >
                        <SvgIcon name={'SvgLeftStrokeArrow'} size={20} stroke={colors.black} fill={colors.trans} />
                    </div>
                    <Subtitle1Bold style={{ color: colors.LAVEL_4 }}>{'반복수업 등록하기'}</Subtitle1Bold>
                    <Body3Regular style={{ color: colors.LAVEL_1, marginLeft: 16 }}>
                        {'매주 일정이 고정적인 회원 전용이에요!'}
                    </Body3Regular>
                </Row>

                <ColorSquareButton
                    isGradient={true}
                    customStyle={{ width: 136, height: 36 }}
                    text="반복일정 등록"
                    size="small"
                    isDisabled={createRepeatSchedule.isLoading}
                    callBack={onClickRegisterRepeatScheduleButton}
                />
            </Row>

            <Row style={{ marginTop: 56 }}>
                <CreateRepeatScheduleLeft
                    coachOption={coachOption}
                    coach={coach}
                    handleCoachSelect={handleCoachSelect}
                    selectedDay={selectedDay}
                    setIsCalendar={setIsCalendar}
                    timeList={timeList}
                    startTime={startTime}
                    handleStartTimeSelect={handleStartTimeSelect}
                    endTime={endTime}
                    handleEndTimeSelect={handleEndTimeSelect}
                    repeatNumber={repeatNumber}
                    onChangeRepeatCount={onChangeRepeatCount}
                    daysArr={daysArr}
                    setDaysArr={setDaysArr}
                    onClickRepeatDay={onClickRepeatDay}
                    isEveryDaySameTime={isEveryDaySameTime}
                    setIsEveryDaySameTime={setIsEveryDaySameTime}
                    roundedTime={roundedTime}
                    currentTimeAdd50={currentTimeAdd50}
                    onClickMaxRepeatCount={onClickMaxRepeatCount}
                    maxRepeatNumber={maxRepeatNumber}
                />
                <CreateRepeatScheduleRight
                    selectedMember={scheduleSelectedMember}
                    filteredMemberList={filteredMemberList}
                    handleMemberSelect={handleMemberSelect}
                    searchText={searchText}
                    onChangeText={onChangeText}
                    onClickDeleteSelectedMember={onClickDeleteSelectedMember}
                    onClickClassPassPage={onClickClassPassPage}
                    coach={coach}
                    currentMemberShipIndex={currentMemberShipIndex}
                    setCurrentMemberShipIndex={setCurrentMemberShipIndex}
                    excludedSharedMembershipCounts={excludedSharedMembershipCounts}
                />
            </Row>

            {isCalendar && (
                <Calendar
                    selectedDay={selectedDay}
                    setSelectedDay={setSelectedDay}
                    setIsCalendar={setIsCalendar}
                    isPrevMonth={true}
                    isNextMonth={true}
                    style={{
                        width: 420,
                        height: 370,
                        borderRadius: 24,
                        backgroundColor: colors.WHITE_50,
                        boxShadow: '1px 1px 8px 0px #2B529D2E',
                        position: 'absolute',
                        zIndex: 1999,
                        marginTop: 340,
                    }}
                />
            )}

            {isModalMembershipCreateVisible && (
                <ModalMembershipCreate
                    setIsVisible={setIsModalMembershipCreateVisible}
                    onClickCreateNewMembership={() => setIsRegisterPassModal(true)}
                    memberName={selectedMemberName}
                    coachName={coach?.label}
                    coachId={coach?.value}
                    memberId={selectedMemberId}
                    createAlert={createAlert}
                    type={'schedule'}
                />
            )}

            {isRegisterPassModal && (
                <ModalRegisterPass
                    modalVisible={isRegisterPassModal}
                    setModalVisible={setIsRegisterPassModal}
                    createAlert={createAlert}
                />
            )}
        </Col>
    );
};

export default CreateRepeatSchedule;
